import { useState } from "react";
import { Reveal } from "./Reveal";
import { SliderField, ResultCard, fmt } from "./CalculatorParts";

const FREQUENCIES = [
  { id: "weekly", label: "Weekly", perYear: 52 },
  { id: "fortnightly", label: "Fortnightly", perYear: 26 },
  { id: "monthly", label: "Monthly", perYear: 12 },
];

export const Calculator = () => {
  const [amount, setAmount] = useState(650000);
  const [rate, setRate] = useState(5.9);
  const [years, setYears] = useState(30);
  const [freq, setFreq] = useState("monthly");

  const perYear = FREQUENCIES.find((f) => f.id === freq).perYear;
  const r = rate / 100 / perYear;
  const n = years * perYear;
  const repayment = r > 0 ? (amount * r) / (1 - Math.pow(1 + r, -n)) : amount / n;
  const totalPaid = repayment * n;

  return (
    <section id="calculator" data-testid="calculator-section" className="py-32 md:py-48">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal>
          <p
            data-testid="calculator-overline"
            className="text-xs font-semibold tracking-[0.35em] uppercase text-[#CFAEA5] mb-6"
          >
            Repayment Calculator
          </p>
          <h2
            data-testid="calculator-heading"
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight text-balance mb-20 md:mb-28"
          >
            Explore the <em className="italic text-[#CFAEA5]">numbers.</em>
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-24 items-start">
          <Reveal className="md:col-span-6">
            <div className="space-y-12">
              <SliderField
                label="Loan amount"
                display={fmt(amount)}
                min={50000}
                max={3000000}
                step={10000}
                value={amount}
                onChange={setAmount}
                testid="calculator-amount"
              />
              <SliderField
                label="Interest rate"
                display={`${rate.toFixed(2)}% p.a.`}
                min={1}
                max={12}
                step={0.05}
                value={rate}
                onChange={setRate}
                testid="calculator-rate"
              />
              <SliderField
                label="Loan term"
                display={`${years} years`}
                min={5}
                max={30}
                step={1}
                value={years}
                onChange={setYears}
                testid="calculator-term"
              />
              <div>
                <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#908B85] mb-4">
                  Repayment frequency
                </p>
                <div className="flex gap-3">
                  {FREQUENCIES.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setFreq(f.id)}
                      data-testid={`calculator-freq-${f.id}`}
                      className={`px-5 py-2.5 rounded-full text-xs font-semibold tracking-[0.2em] uppercase border transition-colors duration-300 ${
                        freq === f.id
                          ? "bg-[#DBB89C] border-[#DBB89C] text-[#FBF7F2]"
                          : "border-black/15 text-[#908B85] hover:border-[#DBB89C] hover:text-[#CFAEA5]"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="md:col-span-5 md:col-start-8">
            <ResultCard
              repayment={repayment}
              freq={freq}
              totalInterest={totalPaid - amount}
              totalPaid={totalPaid}
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
};
